import { SNOWPLOW_TRACKER_NAMESPACE } from '@/lib/snowplow/constants';
import { getInstallSource, type InstallSource } from '@/tauri/install-source';
import {
  getDesktopInstallerType,
  getDistributionChannel,
  TAURI_INSTALLER_TYPE,
  type DISTRIBUTION_CHANNEL,
} from '@/utils/appConfiguration';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import type { SelfDescribingJson } from '@snowplow/browser-tracker';

// Vendor is `com.heidi` (matches the published data structures in oscerai/snowplow
// #141 and every other schema in the codebase — `com.heidihealth` in the ticket was
// a typo). These source-app global-context entities are hand-written here, mirroring
// `client_context` in src/lib/snowplow/index.ts; Snowtype generates events, not these.
const INSTALL_CONTEXT_SCHEMA = 'iglu:com.heidi/install_context/jsonschema/1-0-0';
const DEVICE_MDM_SCHEMA = 'iglu:com.heidi/device_mdm/jsonschema/1-0-0';

/**
 * Readiness latch for the entity array `applyInstallSourceContext` hands to
 * `addGlobalContexts`. `application_install` fires as soon as the install
 * marker is claimed, which is earlier than the async MDM probe resolves, so
 * that one event awaits this latch: the await only orders the
 * `addGlobalContexts` attach before the event fires, and the entities still
 * reach it through the global contexts like every later event's. One
 * resolution per app load: resolving again is a silent no-op.
 */
let resolveReady: ((contexts: SelfDescribingJson[]) => void) | undefined;
const readyPromise = new Promise<SelfDescribingJson[]>((resolve) => {
  resolveReady = resolve;
});

function settleReady(contexts: SelfDescribingJson[]): void {
  resolveReady?.(contexts);
  resolveReady = undefined;
}

/**
 * Resolves with the same entity array attached as Snowplow global contexts, so
 * an early event's entities and every later event's entities cannot drift.
 * Resolves `[]` when the context could not be resolved at all, and
 * already-resolved `[]` on a suppressed build so that build never waits.
 *
 * Callers must bound their own wait: on a flag-off or pre-evaluation launch
 * nothing calls `applyInstallSourceContext`, so this never settles.
 */
export function installSourceContextReady(): Promise<SelfDescribingJson[]> {
  if (!isSaasTelemetryEnabled) return Promise.resolve([]);
  return readyPromise;
}

/**
 * snake_case shape required by the `device_mdm` Iglu schema. The IPC DTO is
 * camelCase (`#[serde(rename_all = "camelCase")]`), and `is_domain_joined` /
 * `is_azure_ad_joined` are required non-null booleans in the schema even though
 * the Rust side models them as `Option<bool>` — hence the `?? false` defaults.
 */
type DeviceMdmEntityData = {
  mdm_provider: string | null;
  mdm_provider_raw: string | null;
  is_domain_joined: boolean;
  is_azure_ad_joined: boolean;
};

function toDeviceMdmEntity(mdm: InstallSource): DeviceMdmEntityData {
  return {
    mdm_provider: mdm.mdmProvider,
    mdm_provider_raw: mdm.mdmProviderRaw,
    is_domain_joined: mdm.isDomainJoined ?? false,
    is_azure_ad_joined: mdm.isAzureAdJoined ?? false,
  };
}

/**
 * Resolved install-source dimensions, published to `installSourceContextAtom`
 * for downstream consumers (notably the Flagsmith traits in
 * `OpenFeatureContextSync`).
 */
export type InstallSourceContext = {
  installerType: TAURI_INSTALLER_TYPE;
  distributionChannel: DISTRIBUTION_CHANNEL;
  /** Normalized MDM provider, or `null` when detection didn't run / found nothing. */
  mdmProvider: string | null;
};

function isWindowsRuntime(): boolean {
  if (typeof navigator === 'undefined') return false;
  const platform = navigator.platform ?? '';
  const userAgent = navigator.userAgent ?? '';
  return /Windows|Win32|Win64|WOW64/i.test(`${platform} ${userAgent}`);
}

/**
 * Resolves the install-source dimensions and attaches them to Snowplow (global
 * context entities) and Datadog RUM + Logs (global context properties). Snowplow
 * is the source of truth and feeds the other analytics destinations (e.g. Amplitude)
 * via its connector, so the FE does not push to them directly. Flagsmith traits are
 * handled separately in `OpenFeatureContextSync` via `installSourceContextAtom`
 * because the codebase exposes no raw Flagsmith client — identity flows through
 * `OpenFeature.setContext`.
 *
 * Returns the resolved context so the caller can publish it to the atom, or
 * `null` when telemetry is suppressed (no providers, no detection).
 *
 * TELEMETRY-GATE: the SaaS analytics SDKs (Snowplow / Datadog) are `import()`-ed
 * dynamically *below* the early-return, never statically at module top.
 * That keeps them out of the suppressed bundle's load path entirely (F1 requires
 * SaaS SDKs be unreachable at bundle time, not merely unexecuted) — the namespace literal
 * comes from the SDK-free `@/lib/snowplow/constants`, and the only static
 * `@snowplow/browser-tracker` reference is a type-only import, erased at compile time.
 */
export async function applyInstallSourceContext(): Promise<InstallSourceContext | null> {
  if (!isSaasTelemetryEnabled) {
    settleReady([]);
    return null;
  }

  try {
    const installerType = getDesktopInstallerType();
    const distributionChannel = getDistributionChannel();

    // MDM detection is Windows-only (Windows enrollment registry + dsregcmd). Run it
    // on every Windows build regardless of distribution_channel: enterprise / MDM-
    // managed devices receive the direct-download MSI (channel DIRECT, typically
    // Intune/SCCM-pushed), so a channel gate would miss them. installer_type is the
    // build-time platform proxy — MSI/NSIS are Windows; DMG/MAS are macOS, where MDM
    // detection is a separate follow-up, so we don't emit `device_mdm` there.
    const isWindowsBuild =
      installerType === TAURI_INSTALLER_TYPE.MSI ||
      installerType === TAURI_INSTALLER_TYPE.NSIS;
    const shouldRunMdmDetection =
      isWindowsBuild ||
      (installerType === TAURI_INSTALLER_TYPE.UNKNOWN && isWindowsRuntime());
    const mdm = shouldRunMdmDetection ? await getInstallSource().catch(() => null) : null;
    const mdmProvider = mdm?.mdmProvider ?? null;

    // Dynamic imports (past the on-prem guard) so the bundler code-splits these
    // SaaS SDKs into async chunks the on-prem entry never references.
    const [snowplowTracker, snowplowLib, { datadogRum }, { datadogLogs }] =
      await Promise.all([
        import('@snowplow/browser-tracker'),
        import('@/lib/snowplow'),
        import('@datadog/browser-rum'),
        import('@datadog/browser-logs'),
      ]);

    // 1) Snowplow — global context entities on the 'heidi' tracker. `install_context`
    //    attaches to every event; `device_mdm` only when detection ran.
    const contexts: SelfDescribingJson[] = [
      {
        schema: INSTALL_CONTEXT_SCHEMA,
        data: {
          installer_type: installerType,
          distribution_channel: distributionChannel,
        },
      },
    ];
    if (mdm) {
      contexts.push({ schema: DEVICE_MDM_SCHEMA, data: toDeviceMdmEntity(mdm) });
    }
    // Resolve before the initialization guard: the array is correct either way,
    // and an awaiting caller is then ordered after the attach below.
    settleReady(contexts);

    // Match the isSnowplowInitialized() guard used by trackSnowplowEvent / the
    // page-view tracker: if the 'heidi' tracker was never created (e.g. no
    // collector URL → initSnowplow early-returns), addGlobalContexts would target a
    // non-existent tracker and silently drop these entities while Datadog still fires.
    if (snowplowLib.isSnowplowInitialized()) {
      snowplowTracker.addGlobalContexts(contexts, [SNOWPLOW_TRACKER_NAMESPACE]);
    }

    // 2) Datadog RUM + Logs — global context properties (observability correlation).
    datadogRum.setGlobalContextProperty('distribution_channel', distributionChannel);
    datadogRum.setGlobalContextProperty('mdm_provider', mdmProvider);
    datadogLogs.setGlobalContextProperty('distribution_channel', distributionChannel);
    datadogLogs.setGlobalContextProperty('mdm_provider', mdmProvider);

    // No Amplitude `identify` here: installer_type / distribution_channel /
    // mdm_provider are device/build dimensions, not user identity. As last-write-wins
    // Amplitude user properties they get clobbered across a user's web/desktop
    // sessions, so they ride as per-event properties via trackEventV2 instead (see
    // getInstallSourceAnalyticsTraits in analyticsScribeModel). bce3749bf0 set them
    // on identify here; reverted — device dimensions don't belong on user identity.

    return { installerType, distributionChannel, mdmProvider };
  } catch (error) {
    // A failed SDK load must not leave a first-launch event waiting out its
    // full timeout; the caller still gets the original rejection.
    settleReady([]);
    throw error;
  }
}
